import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { initials, relativeDays } from '../lib/format';

function LicenseChip() {
  const { caps } = useSession();
  const nav = useNavigate();
  const lic = caps?.license;
  if (!lic) return null;

  if (!lic.provided) {
    return (
      <button className="lic-chip lic-chip--warn" onClick={() => nav('/cumplimiento/licencias')}>
        <span className="lic-chip__pulse" />
        Sin licencia aportada
      </button>
    );
  }

  const days = lic.daysToExpiry;
  const level = days == null ? 'ok' : days < 0 ? 'crit' : days <= 30 ? 'crit' : days <= 60 ? 'warn' : 'ok';
  const label =
    days == null
      ? 'Licencia vigente'
      : days < 0
        ? `Licencia caducada ${relativeDays(days)}`
        : `Licencia · caduca ${relativeDays(days)}`;

  return (
    <button className={`lic-chip lic-chip--${level}`} onClick={() => nav('/cumplimiento/licencias')} title="Licencia comercial">
      <span className="lic-chip__pulse" />
      {label}
    </button>
  );
}

function Alerts() {
  const { company } = useSession();
  const nav = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let live = true;
    if (!company) return;
    api
      .get<Array<{ severity: string }>>('/alerts')
      .then((rows) => {
        if (live) setCount(rows.filter((a) => a.severity === 'critical').length);
      })
      .catch(() => void 0);
    return () => {
      live = false;
    };
  }, [company]);

  return (
    <button className="icon-btn" onClick={() => nav('/cumplimiento/vencimientos')} aria-label="Alertas">
      <Icon name="bell" size={18} />
      {count > 0 && <span className="badge-dot">{count}</span>}
    </button>
  );
}

function UserChip() {
  const { me, logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!me) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="user-chip" onClick={() => setOpen((v) => !v)}>
        <span className="user-chip__avatar">{initials(me.user.name)}</span>
        <span className="user-chip__name">{me.user.name.split(' ')[0]}</span>
        <Icon name="chevron-down" size={13} className="faint" />
      </button>
      {open && (
        <div className="company-menu" style={{ left: 'auto', right: 0, width: 220 }}>
          <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid var(--line)', marginBottom: 4 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{me.user.name}</div>
            <div className="faint" style={{ fontSize: 12 }}>{me.roleLabel} · {me.workspace.name}</div>
          </div>
          <button className="company-menu__item" onClick={logout}>
            <Icon name="external" size={15} className="faint" />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

export function TopBar() {
  const nav = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="topbar">
      <form
        className="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim()) nav(`/buscar?q=${encodeURIComponent(q.trim())}`);
        }}
      >
        <Icon name="search" size={17} />
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar documentos, clientes, operaciones…"
          aria-label="Buscar"
        />
        <span className="search__kbd">⌘K</span>
      </form>

      <div className="topbar__spacer" />

      <LicenseChip />
      <Alerts />
      <UserChip />
    </header>
  );
}
